import React, { useEffect, useRef, useState } from "react";
import styles from "./GiveawayModal.module.css";
import Modal from "../../../../Components/Modal/Modal";
import { useModalStore } from "../../../../Store/ModalStore";
import couponLogo from "/couponLogo.svg";
import minusIcon from "/minus-icon.svg";
import plusIcon from "/plus-icon.svg";
import useUserStore from "../../../../Store/UserStore.js";
import { useWordDeclension } from "../../../../../utils/useWordDeclension";

export enum CouponType {
  DC = "DC",
  STAR = "STAR",
}
interface GiveawayModalProps {
  couponType: CouponType;
  couponPrice: number;
  onPurchase: (amount: number) => void;
  purchasedDC?: number; // Добавляем количество уже купленных DC купонов
}

export default function GiveawayModal({
  couponType,
  couponPrice,
  onPurchase,
  purchasedDC = 0, // По умолчанию 0
}: GiveawayModalProps) {
  const [couponAmount, setCouponAmount] = useState<number>(1);
  const [isFirstInput, setIsFirstInput] = useState<boolean>(true);
  const currentAmountRef = useRef<number>(1);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleClose = useModalStore((state) => state.handleClose);
  const balanceDC = useUserStore((state) => state.balanceDC);

  const count = couponAmount * couponPrice;
  const declension = useWordDeclension(count, {
    one: "звезду",
    few: "звезды",
    many: "звезд",
  });
  const currency = couponType === CouponType.DC ? "DC" : declension;
  const balance = couponType === CouponType.DC ? balanceDC : Infinity;

  const maxDCCoupons = 5;
  const remainingDCCoupons = maxDCCoupons - purchasedDC;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isFirstInput && e.key >= "0" && e.key <= "9") {
      e.preventDefault();
      const value = parseInt(e.key, 10);
      if (couponType === CouponType.DC) {
        const maxAmount = Math.min(
          Math.floor(balance / couponPrice),
          remainingDCCoupons
        );
        setCouponAmount(Math.min(value, maxAmount));
      } else {
        // Для STAR купонов просто устанавливаем введенное значение
        setCouponAmount(value);
      }
      setIsFirstInput(false);
    }
  };

  // Эффект для синхронизации состояния с инпутом
  useEffect(() => {
    if (inputRef.current) {
      const value = parseInt(inputRef.current.value, 10);
      if (!isNaN(value) && value > 0) {
        if (couponType === CouponType.DC) {
          const maxAmount = Math.min(
            Math.floor(balance / couponPrice),
            remainingDCCoupons
          );
          setCouponAmount(Math.min(value, maxAmount));
        } else {
          setCouponAmount(value);
        }
      }
    }
  }, [inputRef.current, couponType, balance, couponPrice, remainingDCCoupons]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === "") {
      const defaultValue = couponType === CouponType.DC ? 1 : 5;
      setCouponAmount(defaultValue);
      currentAmountRef.current = defaultValue;
      setIsFirstInput(true);
      return;
    }

    const value = parseInt(inputValue, 10);

    if (isNaN(value) || value <= 0) {
      const defaultValue = couponType === CouponType.DC ? 1 : 5;
      setCouponAmount(defaultValue);
      currentAmountRef.current = defaultValue;
      setIsFirstInput(true);
      return;
    }

    if (couponType === CouponType.DC) {
      const maxAmount = Math.min(
        Math.floor(balance / couponPrice),
        remainingDCCoupons
      );
      const newValue = Math.min(value, maxAmount);
      setCouponAmount(newValue);
      currentAmountRef.current = newValue;
    } else {
      setCouponAmount(value);
      currentAmountRef.current = value;
    }
  };

  const handleBlur = () => {
    if (couponType === CouponType.STAR) {
      // Округляем до ближайшего числа, кратного 5, только при потере фокуса
      setCouponAmount(Math.min(couponAmount, 9999));
    }
  };

  const handleQuickSelect = (amount: number) => {
    if (couponType === CouponType.DC) {
      const maxAmount = Math.min(
        Math.floor(balance / couponPrice),
        remainingDCCoupons
      );
      const newValue = Math.min(amount, maxAmount);
      setCouponAmount(newValue);
      currentAmountRef.current = newValue;
    } else {
      setCouponAmount(amount);
      currentAmountRef.current = amount;
    }
  };

  // Эффект для инициализации кнопки
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    tg.MainButton.show();
    tg.setBottomBarColor("#0C0214");

    const handleSubmit = () => {
      const canPurchase =
        couponType === CouponType.DC
          ? count <= balance && couponAmount <= remainingDCCoupons
          : true;

      if (canPurchase) {
        console.log("Purchasing:", {
          couponType,
          amount: currentAmountRef.current,
          stars: currentAmountRef.current,
          inputValue: inputRef.current?.value,
        });

        const finalAmount =
          couponType === CouponType.DC
            ? currentAmountRef.current
            : parseInt(inputRef.current?.value || "1", 10);

        onPurchase(finalAmount);
        handleClose();
      }
    };

    tg.MainButton.onClick(handleSubmit);

    return () => {
      tg.MainButton.offClick(handleSubmit);
      tg.MainButton.hide();
    };
  }, [couponType, balance, remainingDCCoupons, onPurchase, handleClose]);

  // Эффект для обновления текста кнопки
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    const canPurchase =
      couponType === CouponType.DC
        ? count <= balance && couponAmount <= remainingDCCoupons
        : true;

    tg.MainButton.setParams({
      text: `Участвовать за ${count} ${currency}`,
      color: canPurchase ? "#8B5CF6" : "#737373",
      is_active: canPurchase,
      is_visible: true,
    });
  }, [
    couponAmount,
    couponPrice,
    currency,
    couponType,
    balance,
    remainingDCCoupons,
  ]);

  const handleIncrease = () => {
    if (couponType === CouponType.DC) {
      const maxAmount = Math.min(
        Math.floor(balance / couponPrice),
        remainingDCCoupons
      );
      if (couponAmount < maxAmount) {
        const newValue = couponAmount + 1;
        setCouponAmount(newValue);
        currentAmountRef.current = newValue;
      }
    } else {
      const newValue = couponAmount + 1;
      setCouponAmount(newValue);
      currentAmountRef.current = newValue;
    }
  };

  const handleDecrease = () => {
    const minValue = 1;
    if (couponAmount > minValue) {
      const newValue = couponAmount - 1;
      setCouponAmount(newValue);
      currentAmountRef.current = newValue;
    }
  };

  return (
    <Modal>
      <div className={styles.modalContainer}>
        <h2 className={styles.modalTitle}>Введи количество купонов</h2>
        <p className={styles.modalSubtitle}>1 купон = 1 шанс выигрыша</p>

        {couponType === CouponType.DC && (
          <div className={styles.balanceInfo}>Твой баланс: {balance} DC</div>
        )}

        <div
          className={
            styles.couponSelector +
            " " +
            (couponType === CouponType.DC
              ? styles.couponSelectorDC
              : styles.couponSelectorStar)
          }
        >
          <button
            className={styles.counterButton}
            onClick={handleDecrease}
            disabled={couponAmount <= 1}
          >
            <img src={minusIcon} alt="minus" />
          </button>

          <div className={styles.inputWrapper}>
            <input
              ref={inputRef}
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              className={styles.couponInput}
              value={couponAmount}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              maxLength={4}
            />
            <img
              src={couponLogo}
              alt={currency}
              className={styles.currencyIcon}
            />
          </div>

          <button
            className={styles.counterButton}
            onClick={handleIncrease}
            disabled={
              couponType === CouponType.DC &&
              couponAmount >=
                Math.min(Math.floor(balance / couponPrice), remainingDCCoupons)
            }
          >
            <img src={plusIcon} alt="plus" />
          </button>
        </div>

        {remainingDCCoupons > 0 && couponType === CouponType.DC && (
          <div className={styles.remainingInfo}>
            Нельзя купить за DC больше 5 купонов в одном розыгрыше
          </div>
        )}

        <div className={styles.quickSelect}>
          <button
            className={`${styles.quickButton} ${
              couponAmount === (couponType === CouponType.DC ? 1 : 5)
                ? styles.quickButtonActive
                : ""
            }`}
            onClick={() =>
              handleQuickSelect(couponType === CouponType.DC ? 1 : 5)
            }
            disabled={couponType === CouponType.DC && remainingDCCoupons < 1}
          >
            <span>{couponType === CouponType.DC ? 1 : 5}</span>
            <img src={couponLogo} alt={currency} />
          </button>

          <button
            className={`${styles.quickButton} ${
              couponAmount === (couponType === CouponType.DC ? 3 : 15)
                ? styles.quickButtonActive
                : ""
            }`}
            onClick={() =>
              handleQuickSelect(couponType === CouponType.DC ? 3 : 15)
            }
            disabled={couponType === CouponType.DC && remainingDCCoupons < 3}
          >
            <span>{couponType === CouponType.DC ? 3 : 15}</span>
            <img src={couponLogo} alt={currency} />
          </button>

          <button
            className={`${styles.quickButton} ${
              couponAmount === (couponType === CouponType.DC ? 5 : 25)
                ? styles.quickButtonActive
                : ""
            }`}
            onClick={() =>
              handleQuickSelect(couponType === CouponType.DC ? 5 : 25)
            }
            disabled={couponType === CouponType.DC && remainingDCCoupons < 5}
          >
            <span>{couponType === CouponType.DC ? 5 : 25}</span>
            <img src={couponLogo} alt={currency} />
          </button>
        </div>
      </div>
    </Modal>
  );
}
